import { z } from "zod";
import { isValidPhoneNumber } from "react-phone-number-input";

export type CreateOrderSchema = z.infer<typeof createOrderSchema>;
export const createOrderSchema = z.object({
  placerCustomerId: z.string().optional().nullable(),
  senderCustomerId: z.string().optional(),
  senderCustomerNumber: z.string().optional(),
  recipientCustomerId: z.string().optional(),
  recipientCustomerNumber: z.string().optional(),
  type: z.enum(["STANDARD", "EXPRESS", "SCHEDULED", "SAME_DAY"]),
  status: z.enum([
    "PENDING",
    "PICKUP_ASSIGNED",
    "PICKED_UP",
    "AT_DEPOT",
    "SORTED",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "FAILED",
    "CANCELLED",
    "FIRST_ATTEMPT_FAILED",
    "SECOND_ATTEMPT_FAILED",
    "RETURNED",
  ]),
  recipientName: z.string().nonempty("Recipient name is required"),
  recipientEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  recipientPhone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  deliveryAddress: z.object({
    line1: z.string().nonempty("Delivery address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z.string().nonempty("Postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  senderName: z.string().nonempty("Sender name is required"),
  senderEmail: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
  senderPhone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  pickupAddress: z.object({
    line1: z.string().nonempty("Pickup address is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    postalCode: z
      .string()
      .nonempty("Pickup postal code is required")
      .min(1, "Pickup postal code is required"),
    country: z.string().nonempty("Country is required"),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  parcelData: z.array(
    z.object({
      weight: z.coerce.number().optional(),
      dimensions: z
        .object({
          length: z.coerce.number(),
          width: z.coerce.number(),
          height: z.coerce.number(),
        })
        .optional(),
      isFragile: z.boolean().optional(),
      requiresCooling: z.boolean().optional(),
    })
  ),
  customerType: z.enum(["INDIVIDUAL", "BUSINESS"]).default("INDIVIDUAL"),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
});

export type CreateDepotSchema = z.infer<typeof createDepotSchema>;
export const createDepotSchema = z.object({
  name: z.string().nonempty("Name is required"),
  type: z.enum(["HUB", "WAREHOUSE", "MICRO_HUB"]),
  capacity: z.object({
    small: z.number().min(0),
    medium: z.number().min(0),
    large: z.number().min(0),
  }),
  currentCapacity: z.object({
    small: z.number().min(0),
    medium: z.number().min(0),
    large: z.number().min(0),
  }),
  serviceRadius: z.number().optional(),
  operatingHours: z.array(
    z.object({
      day: z.number().min(1).max(7),
      open: z.string(),
      close: z.string(),
    })
  ),
  address: z.object({
    line1: z.string().nonempty("Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    country: z.string().nonempty("Country is required"),
    postalCode: z.string().optional(),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    plusCode: z.string().optional(),
  }),
});

export type CreateInvoiceSchema = z.infer<typeof createInvoiceSchema>;
export const createInvoiceSchema = z.object({
  customerId: z.string().nonempty("Customer is required"),
  orderIds: z.array(z.string()).min(1, "At least one order must be selected"),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  dueDate: z.date(),
  discount: z
    .object({
      type: z.enum(["percentage", "amount"]),
      value: z.number().min(0).max(100000), // Max $1000 for amount or 100% for percentage
    })
    .optional(),
});

export type CreateOperatorSchema = z.infer<typeof createOperatorSchema>;
export const createOperatorSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .nonempty("First name is required"),
  lastName: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .nonempty("Last name is required"),
  email: z
    .string()
    .nonempty("Email is required")
    .email("Invalid email address"),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  dateOfBirth: z
    .string()
    .nonempty("Date of birth is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)"),
  documents: z.object({
    license: z.string().default("NOT_PROVIDED"),
    insurance: z.string().default("NOT_PROVIDED"),
  }),
  workSchedule: z
    .array(
      z.object({
        day: z
          .number()
          .min(1, "Day must be between 1 and 7")
          .max(7, "Day must be between 1 and 7"),
        start: z.string().nonempty("Start time is required"),
        end: z.string().nonempty("End time is required"),
      })
    )
    .min(1, "At least one work schedule is required"),
  depotId: z.string().nonempty("Depot is required"),
  authMethod: z.enum(["sms", "email"]).default("sms"),
  address: z.object({
    line1: z.string().nonempty("Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    country: z.string().nonempty("Country is required"),
    postalCode: z.string().optional(),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});
